/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { initialOptions } from 'src/constants/initialOptionsApi';
import { useShowSnackbar } from './useShowSnackbar';

export type ApiPostDataType<T> = Record<string, T | FormData>;

interface ApiPostState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  postData: (data: ApiPostDataType<T>) => Promise<T>;
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const useApi = <T>(
  url: string,
  method: string = 'POST',
  initialData?: T,
  timeout: number = 1800000, // Default timeout of 10 seconds
): ApiPostState<T> => {
  const [data, setData] = useState<T | null>(initialData || null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const showSnackbar = useShowSnackbar();

  const postData = async (postData: ApiPostDataType<T>) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      setLoading(true);

      const isFormData = postData instanceof FormData;
      const body = isFormData ? postData : JSON.stringify(postData);

      const response = await fetch(`${url}`, {
        ...initialOptions,
        method,
        body,
        signal: controller.signal,
        headers: isFormData
          ? {
              Authorization: process.env.REACT_APP_API_KEY || '',
            }
          : (initialOptions.headers as HeadersInit),
      });

      if (!response.ok) {
        showSnackbar(`API error: ${response.statusText}`, 'error');
      }

      const responseData = await response.json();
      setData(responseData);
      return responseData;
    } catch (error) {
      if (controller.signal.aborted) {
        const timeoutError = new Error('Request timed out');
        setError(timeoutError);
        showSnackbar(timeoutError.message, 'error');
      } else {
        const err = error as Error;
        setError(err);

        // Network-level failures (ERR_HTTP2_PROTOCOL_ERROR, ERR_NETWORK, etc.)
        const isNetworkError = err.name === 'TypeError' && !navigator.onLine === false;
        const message =
          err.name === 'TypeError'
            ? 'Network error — please check your connection and try again.'
            : err.message || 'An unexpected error occurred.';

        showSnackbar(message, 'error');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      postData(initialData);
    }

    return () => setData(null);
  }, [url, initialData]);

  return { data, error, loading, postData };
};

export default useApi;
